import React, { useState } from 'react';
import { TouchableOpacity } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { LabeledDivider, Text } from '@/components/ui';
import { hitSlop } from '@/constants';
import { STRINGS } from '@/constants/strings';
import {
  AuthBackButton,
  AuthBrandLogo,
  AuthLegalPopup,
  AuthPrimaryButton,
  AuthScreenBackground,
  AuthTermsText,
  AuthTextField,
} from '@/features/auth/components';
import { SocialAuthButton } from '@/features/auth/components/social-auth-button';
import { useLegalPopup } from '@/features/auth/hooks/use-legal-popup';
import { useLogin } from '@/features/auth/hooks/use-login';
import { createStyles, useScaling, useUnAuthStackNavigation } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

export function LoginScreen() {
  const { scale, isTablet } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useUnAuthStackNavigation();
  const legal = useLegalPopup();

  const { email, onChangeEmail, password, onChangePassword, handleSubmit, errors } = useLogin();

  const [secureTextEntry, setSecureTextEntry] = useState(true);

  const strings = STRINGS.auth.login;

  const handleBack = () => {
    navigation.goBack();
  };

  const handleForgotPassword = () => {
    navigation.navigate('ForgotPassword');
  };

  const top = insets.top > 0 ? insets.top + scale(16) : scale(50);

  return (
    <AuthScreenBackground scrollable>
      <Stack width={isTablet ? '100%' : scale(335)}>
        <Stack
          left={isTablet ? scale(50) : undefined}
          paddingTop={top}
          style={isTablet ? styles.backButtonTabletContainer : styles.backButtonContainer}>
          <AuthBackButton onPress={handleBack} />
        </Stack>
      </Stack>
      <Stack
        flex={1}
        width={scale({ phone: 335, tablet: 380 })}
        style={{
          paddingTop: isTablet ? top + scale(18) : undefined,
          paddingBottom: insets.bottom + scale(12),
        }}>
        {/* Logo */}
        <Stack align="center" marginTop={scale({ phone: 0, tablet: 0 })}>
          <AuthBrandLogo portrait width={scale(154)} height={scale(84)} />
        </Stack>

        {/* Title */}
        <Text
          variant={
            isTablet
              ? 'Font Guardian App/Heading/Heading H5 24px'
              : 'Font Guardian App/Heading/Heading H6 18px'
          }
          style={styles.title}
          accessibilityRole="header">
          {strings.title}
        </Text>

        {/* Form */}
        <Stack marginTop={scale({ phone: 32, tablet: 24 })} gap={scale(16)}>
          <AuthTextField
            label={strings.labelEmail}
            iconName="IcSms"
            value={email}
            onChangeText={onChangeEmail}
            required
            error={errors.email?.message}
            placeholder={strings.placeholderEmail}
            fieldProps={{
              keyboardType: 'email-address',
              autoComplete: 'username',
              textContentType: 'username',
              trimOnBlur: true,
              maxLength: 100,
            }}
          />

          <AuthTextField
            label={strings.labelPassword}
            iconName="IcPasswordCheck"
            value={password}
            onChangeText={onChangePassword}
            required
            error={errors.password?.message}
            placeholder={strings.placeholderPassword}
            secureTextEntry={secureTextEntry}
            onSecureTextEntryChange={setSecureTextEntry}
            fieldProps={{ maxLength: 50 }}
          />
        </Stack>

        {/* Forgot password */}
        <TouchableOpacity
          onPress={handleForgotPassword}
          hitSlop={hitSlop}
          style={styles.forgotWrapper}>
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.forgotText}>
            {strings.forgotPassword}
          </Text>
        </TouchableOpacity>

        {/* Login */}
        <AuthPrimaryButton onPress={handleSubmit} style={styles.loginButton}>
          {strings.buttonLogin}
        </AuthPrimaryButton>

        {/* Flexible spacer pushes the social actions to the bottom */}
        <Stack flex={1} minHeight={scale(40)} />

        {/* Or login with */}
        <LabeledDivider
          color={colors['3']}
          textVariant="Font Guardian App/Subtitle/Subtitle 12 Regular"
          textColor={colors['6']}>
          {strings.orLoginWith}
        </LabeledDivider>

        {/* Social auth */}
        <Stack direction="row" justify="center" gap={scale(16)} marginTop={scale(16)}>
          <SocialAuthButton provider="google" />
          <SocialAuthButton provider="apple" />
        </Stack>

        {/* Terms */}
        <AuthTermsText
          prefix={strings.termsPrefix}
          termsOfService={strings.termsOfService}
          conjunction={strings.termsConjunction}
          privacyPolicy={strings.privacyPolicy}
          onPressTermsOfService={legal.openTerms}
          onPressPrivacyPolicy={legal.openPrivacy}
          breakAfterPrefix
          style={styles.terms}
        />
      </Stack>

      <AuthLegalPopup visible={legal.visible} document={legal.document} onClose={legal.close} />
    </AuthScreenBackground>
  );
}

const useStyles = createStyles(({ scale }) => ({
  backButtonContainer: {
    alignSelf: 'flex-start',
  },
  backButtonTabletContainer: {
    position: 'absolute',
  },
  title: {
    marginTop: scale({ phone: 45, tablet: 40 }),
    color: colors['10'],
    textAlign: 'center',
  },
  forgotWrapper: {
    alignSelf: 'flex-end',
    marginTop: scale(12),
  },
  forgotText: {
    color: guardianColors['Main/Main'],
    textAlign: 'right',
  },
  loginButton: {
    marginTop: scale(24),
  },
  terms: {
    marginTop: scale(30),
  },
}));

export default LoginScreen;
